import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Image from 'quarks/Image';
import Link from 'quarks/Link';
import Paragraph from 'quarks/Paragraph';
import Text from 'quarks/Text';

import { minutesToRead } from 'utils/timeToRead';

import type { ContentfulAsset, ContentfulTemplateCompanyNews } from 'graphqlTypes';
import type { FC } from 'react';

// Similar to ResultCard from Search page, but different enough to use separate component
const CompanyNewsCard: FC<ContentfulTemplateCompanyNews> = ({
  publishDate,
  slug,
  title,
  featuredImage,
  synopsis,
  body,
}) => {
  const date =
    publishDate &&
    new Date(publishDate).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  const url = `https://www.justworks.com/press/company-news/${slug}`;
  const eyebrow = 'Company News';
  const timeToRead = body && minutesToRead(body);

  return (
    <Link
      href={url || ''}
      textColor="gray-700"
      textDecoration="none"
      borderRadius="8px"
      paddingAll={16}
      hover={{
        backgroundColor: 'gray-100',
      }}
      focusVisible={{ backgroundColor: 'gray-100' }}
      css="margin: -16px;"
    >
      <Flex alignItems="center" gap="16px" flexDirection="column" sm={{ flexDirection: 'row' }}>
        <Image
          image={featuredImage as ContentfulAsset} // allow it to default to Justworks banner if undefined
          minHeight="160px"
          width="100%"
          alt={featuredImage?.title || ''}
          objectFit="cover"
          sm={{ maxWidth: '50%' }}
          md={{ maxWidth: '35%' }}
          xl={{ maxWidth: '370px' }}
        />
        <Flex flexDirection="column" gap="8px" width="100%">
          <Text
            textStyle="eyebrowSm"
            fontWeight="extraBold"
            textColor="red-600"
            lg={{ textStyle: 'eyebrowLg', fontWeight: 'extraBold' }}
          >
            {eyebrow}
          </Text>
          <Flex flexDirection="column" gap="4px">
            <Heading
              as="h3"
              fontSize="textMd"
              lineHeight="textMd"
              fontWeight="bold"
              alignSelf="flex-start"
              textColor="common-black"
              lineClamp={2}
              md={{ fontSize: 'textLg', lineHeight: 'textLg', fontWeight: 'bold' }}
              lg={{ textStyle: 'sm' }}
            >
              {title}
            </Heading>
            {synopsis?.synopsis && (
              <Paragraph textStyle="sm" lineClamp={3} sm={{ lineClamp: 2 }} lg={{ textStyle: 'lg' }}>
                {synopsis.synopsis}
              </Paragraph>
            )}
          </Flex>
          {(date || timeToRead) && (
            <Text textStyle="sm" fontWeight="bold" textColor="gray-700">
              {date} {date && timeToRead && '•'} {timeToRead}
            </Text>
          )}
        </Flex>
      </Flex>
    </Link>
  );
};

export default CompanyNewsCard;
